<template>
  <component
    :is="sliderComponent"
    :model-value="modelValue"
    v-bind="$attrs"
    @update:model-value="$emit('update:modelValue', $event)"
    @start="onStart"
    @end="onEnd"
    @keydown="onKeydown"
    @keyup="onKeyup"
  >
    <template
      v-for="(_, name) in $slots"
      #[name]="slotProps"
    >
      <slot
        :name="name"
        v-bind="slotProps ?? {}"
      />
    </template>
  </component>
</template>

<script>
import { VRangeSlider, VSlider } from 'vuetify/components'
import { debounce, isEqual } from '@/libs/lodash'

// the keys that change a slider value. Vuetify emits no
// `end` for keyboard input, so the keyup path commits on its own.
const SLIDER_KEYS = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End', 'PageUp', 'PageDown']

export default {
  name: 'SSlider',
  inheritAttrs: false,
  props: {
    modelValue: {
      type: [Number, Array],
      default: 0,
    },
    // Renders v-range-slider instead of v-slider.
    range: {
      type: Boolean,
      default: false,
    },
    // Wait for the keyboard path only; a pointer release commits at once.
    keyDebounce: {
      type: Number,
      default: 500,
    },
  },
  // `change` fires once per committed value, never during a drag.
  emits: ['update:modelValue', 'change'],
  data () {
    return {
      dragging: false,
      valueAtStart: null,
      // Set on the first key of a burst, so a run of arrow presses commits once
      keysPending: false,
      valueBeforeKeys: null,
      commitKeyChange: debounce(() => {
        const before = this.valueBeforeKeys
        this.resetKeys()
        if (!isEqual(this.modelValue, before)) {
          this.$emit('change', this.modelValue)
        }
      }, this.keyDebounce),
    }
  },
  computed: {
    // Kept out of data(): a component made reactive triggers a Vue warning
    sliderComponent () {
      return this.range ? VRangeSlider : VSlider
    },
  },
  beforeUnmount () {
    // Flush, not cancel: a pending commit holds an edit the user already made
    this.commitKeyChange.flush()
  },
  methods: {
    resetKeys () {
      this.keysPending = false
      this.valueBeforeKeys = null
    },
    onStart (value) {
      this.dragging = true
      this.valueAtStart = value
    },
    onEnd (value) {
      this.commitKeyChange.cancel()
      this.resetKeys()
      // Vuetify registers the window mouseup listener even when the press found
      // no thumb, so `end` can arrive without a matching `start`
      if (!this.dragging) {
        return
      }
      this.dragging = false
      // A press that does not move the thumb still emits `end`
      if (!isEqual(value, this.valueAtStart)) {
        this.$emit('change', value)
      }
    },
    onKeydown (e) {
      if (!SLIDER_KEYS.includes(e.key) || this.keysPending) {
        return
      }
      this.keysPending = true
      this.valueBeforeKeys = this.modelValue
    },
    onKeyup (e) {
      // A readonly slider, or a key pressed at min/max, leaves the value alone;
      // the debounced commit compares before emitting
      if (this.keysPending && SLIDER_KEYS.includes(e.key)) {
        this.commitKeyChange()
      }
    },
  },
}
</script>
